import { Box } from '@mui/material';
import { keyframes } from '@mui/system';
import { styled } from '@mui/material/styles';
import PageMetadata from '../SEO/PageMetadata';

// Load from the app's public folder and respect configured base paths.
const attendInMark = `${import.meta.env.BASE_URL}logo-only.png`;

// ----------------------------------------------------------------------
// Brand colours pulled straight from the logo gradient
const PURPLE = '#493193';
const CYAN = '#2fadd8';

export type LoadingAnimationProps = {
    size?: number; // diameter of the mark in px
    isHeight?: boolean;
};

// ── keyframes ───────────────────────────────────────────────────────
const enter = keyframes`
    0%   { opacity: 0; transform: scale(0.62); filter: blur(6px); }
    60%  { opacity: 1; }
    100% { opacity: 1; transform: scale(1); filter: blur(0); }
`;

const breathe = keyframes`
    0%, 100% { transform: scale(1); }
    50%      { transform: scale(1.035); }
`;

const ping = keyframes`
    0%   { opacity: 0.55; transform: scale(0.55); }
    70%  { opacity: 0;    transform: scale(1.9); }
    100% { opacity: 0;    transform: scale(1.9); }
`;

const slide = keyframes`
    0%   { transform: translateX(-130%); }
    100% { transform: translateX(330%); }
`;

const rise = keyframes`
    to { opacity: 1; transform: translateY(0); }
`;

// ── styled atoms ────────────────────────────────────────────────────
const Ring = styled('span')<{ size: number }>(({ size }) => ({
    position: 'absolute',
    top: '50%',
    left: '50%',
    width: size,
    height: size,
    marginTop: -size / 2,
    marginLeft: -size / 2,
    borderRadius: '50%',
    border: `1.5px solid ${CYAN}`,
    opacity: 0,
    transform: 'scale(0.55)',
    animation: `${ping} 3s cubic-bezier(.22,.61,.36,1) infinite`,
    '@media (prefers-reduced-motion: reduce)': { display: 'none' },
}));

const Mark = styled('div')<{ size: number }>(({ size }) => ({
    position: 'relative',
    zIndex: 2,
    width: size,
    height: size,
    opacity: 0,
    transform: 'scale(0.62)',
    filter: 'blur(6px)',
    animation: `${enter} 0.95s cubic-bezier(.34,1.56,.64,1) forwards, ${breathe} 3.4s ease-in-out 1s infinite`,
    '& img': { width: '100%', height: '100%', display: 'block' },
    '@media (prefers-reduced-motion: reduce)': {
        animation: `${enter} 0.6s ease forwards`,
    },
}));

const Word = styled('div')({
    marginTop: 30,
    fontSize: 13,
    letterSpacing: '0.32em',
    paddingLeft: '0.32em',
    textTransform: 'uppercase',
    fontWeight: 600,
    fontFamily: `ui-rounded, 'Segoe UI', system-ui, sans-serif`,
    background: `linear-gradient(90deg, ${PURPLE}, ${CYAN})`,
    WebkitBackgroundClip: 'text',
    backgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    color: 'transparent',
    opacity: 0,
    transform: 'translateY(8px)',
    animation: `${rise} 0.7s ease-out 0.65s forwards`,
});

const Track = styled('div')({
    marginTop: 22,
    width: 180,
    height: 4,
    borderRadius: 999,
    background: 'rgba(73,49,147,0.10)',
    overflow: 'hidden',
    opacity: 0,
    animation: `${rise} 0.7s ease-out 0.9s forwards`,
});

const Bar = styled('div')({
    width: '42%',
    height: '100%',
    borderRadius: 999,
    background: `linear-gradient(90deg, ${PURPLE}, ${CYAN})`,
    animation: `${slide} 1.5s cubic-bezier(.65,.05,.36,1) infinite`,
    '@media (prefers-reduced-motion: reduce)': { width: '100%', animation: 'none' },
});

// ----------------------------------------------------------------------
export function SplashScreen({ size = 150, isHeight }: LoadingAnimationProps) {
    return (
        <>
            <PageMetadata
                title="Loading | AttendIn"
                description="Loading page content on AttendIn."
            />
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: isHeight ? '50vh' : '100vh',
                    width: '100%',
                }}
            >
            <Box
                sx={{
                    position: 'relative',
                    width: size * 2,
                    height: size * 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <Ring size={size} sx={{ borderColor: CYAN }} />
                <Ring size={size} sx={{ borderColor: PURPLE, animationDelay: '1s' }} />
                <Ring size={size} sx={{ borderColor: CYAN, animationDelay: '2s' }} />
                <Mark size={size}>
                    <img src={attendInMark} alt="AttendIn" />
                </Mark>
            </Box>

            <Word>AttendIn</Word>

            <Track>
                <Bar />
            </Track>
            </Box>
        </>
    );
}

export default SplashScreen;